import React, { useState } from 'react';
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Container, IconButton, Pagination, LinearProgress, Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CloudUpload, Save, Delete, SaveAlt, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { saveContact } from '../api/actions/mutation';
import { useDispatch, useSelector } from 'react-redux';
import { messageShow } from '../../common/api/actions';
import { formatAssigneeName } from '../../../setup/helpers';

const BulkCreate = () => {
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [page, setPage] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveProgress, setSaveProgress] = useState(0);
    const [rowStatus, setRowStatus] = useState({});
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [uploadResults, setUploadResults] = useState({ success: [], failed: [] });
    const rowsPerPage = 20;
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings?.settings);
    const user = useSelector((state) => state.auth?.user);
    const isStoreSubscribed = settings?.isStoreSubscribed;

    const downloadTemplate = () => {
        let headers = [
            'Store Name',
            'Contact Name',
            'Phone Number',
            'Email',
            'Address',
            'Pin Code',
            'Region',
            'Lead Status',
            'Assigned To',
            'Lead Source',
            'Customer Type',
            'Tags',
            'Business Size',
            'Business Description',
            'Industry Type',
            'Business Website',
            'Social Media Links',
            'Year Established',
            'Employee Count',
            'Store Growth Stage',
            'Products & Services Offered'
        ];

        let dummyRow = [
            '"Example Store"',
            '"John Doe"',
            '"+1-555-123-4567"',
            '"john.doe@example.com"',
            '"123 Example Street, Suite 100"',
            '"12345"',
            '"New York"',
            '"Contact/Lead/Prospect/Customer"',
            '"John Doe"',
            '"Manual/Google Search/LinkedIn/Website/Referral/Campaign/Other"',
            '"Candidate/Company"',
            '"tag1,tag2,tag3"',
            '"Small/Medium/Large/Masterclass Registration"',
            '"Retail store selling electronics"',
            '"Retail/Technology/Food & Beverage/Fashion"',
            '"https://www.examplestore.com"',
            '"https://facebook.com/example,https://instagram.com/example"',
            '"2015"',
            '"10"',
            '"Startup/Growing/Established/Enterprise"',
            '"Electronics;Accessories;Repair Services"'
        ];

        const csvContent = headers.join(',') + '\n' + dummyRow.join(',') + '\n';
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'contacts_template.csv';
        link.click();
    };

    // Proper CSV parser that handles empty fields correctly
    const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    current += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        
        // Add last field
        result.push(current.trim());
        
        return result;
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(line => line.trim());
            
            if (lines.length === 0) {
                dispatch(messageShow("CSV file is empty"));
                return;
            }
            
            // Parse headers
            const headerLine = lines[0];
            const parsedHeaders = parseCSVLine(headerLine);
            
            // Parse data rows
            const data = lines.slice(1).map((line, index) => {
                const values = parseCSVLine(line);
                
                // Create object with proper field mapping
                const rowData = parsedHeaders.reduce((obj, header, colIndex) => {
                    let value = values[colIndex] || ''; // Handle empty fields
                    
                    // Remove surrounding quotes if present
                    value = value.replace(/^"(.*)"$/, '$1').trim();

                    // Special handling for specific fields
                    switch(header) {
                        case 'Annual Revenue':
                            obj[header] = /^\d+$/.test(value) ? value : '';
                            break;
                        default:
                            obj[header] = value;
                    }
                    return obj;
                }, {});
                
                return rowData;
            });

            setHeaders(parsedHeaders);
            setCsvData(data);
            setRowStatus({});
            setSaveProgress(0);
            setPage(1);
            dispatch(messageShow(`Successfully loaded ${data.length} rows from CSV`));
        };

        reader.readAsText(file);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const validateRow = (rawData) => {
        const errors = [];
        
        // Email validation
        if (rawData['Email'] && rawData['Email'].trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(rawData['Email'])) {
                errors.push('Invalid email format');
            }
        }
        
        return errors;
    };

    const formatRowData = (rawData) => {
        const currentUserName = formatAssigneeName(user?.name || user?.email || '');
        const rowId = (rawData['Contact ID'] || rawData['_id'] || rawData['Id'] || '').trim();
        const rawAssigned = rawData['Assigned To'] || rawData['assignedTo'] || currentUserName || '';
        const assignedName = formatAssigneeName(rawAssigned);
        return {
            ...(rowId ? { _id: rowId } : {}),
            storeName: rawData['Store Name'] || '',
            contactName: rawData['Contact Name'] || '',
            phoneNumber: rawData['Phone Number'] || '',
            image: rawData['Image'] || '',
            email: rawData['Email'] || rawData['email'] || '',
            address: rawData['Address'] || '',
            pincode: rawData['Pin Code'] || '',
            leadStatus: rawData['Lead Status'] || '',
            assignedTo: assignedName,
            leadSource: rawData['Lead Source'] || rawData['leadSource'] || 'Manual',
            tags: rawData['Tags'] ? rawData['Tags'].split(',').map((t) => t.trim()).filter(Boolean) : [],
            region: rawData['Region'] || '',
            customerType: rawData['Customer Type'] || '',
            businessType: rawData['Business Size'] || "",
            businessDescription: rawData['Business Description'] || '',
            industryType: rawData['Industry Type'] || '',
            businessWebsite: rawData['Business Website'] || '',
            socialMediaLinks: {
                Facebook: rawData['Social Media Links']?.split(',')[0]?.trim() || '',
                Instagram: rawData['Social Media Links']?.split(',')[1]?.trim() || ''
            },
            yearEstablished: rawData['Year Established'] || '',
            employeeCount: rawData['Employee Count'] || '',
            storeGrowthStage: rawData['Store Growth Stage'] || '',
            productsServicesOffered: rawData['Products & Services Offered'] ? 
                rawData['Products & Services Offered'].split(';').map(item => item.trim()) : [],
            type: "Create",
            businessHours: {
                Monday: { start: null, end: null },
                Tuesday: { start: null, end: null },
                Wednesday: { start: null, end: null },
                Thursday: { start: null, end: null },
                Friday: { start: null, end: null },
                Saturday: { start: null, end: null },
                Sunday: { start: null, end: null },
            },
            message: rawData['Message'] || '',
        };
    };

    const handleSaveRow = async(rowIndex) => {
        try {
            setRowStatus(prev => ({ ...prev, [rowIndex]: 'saving' }));
            
            const rawData = csvData[rowIndex];
            
            // Validate row
            const errors = validateRow(rawData);
            if (errors.length > 0) {
                setRowStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
                dispatch(messageShow(`Row ${rowIndex + 1} validation failed: ${errors.join(', ')}`));
                return;
            }
            
            const formattedData = formatRowData(rawData);
            
            const response = await saveContact(formattedData);
            if(response.data.success){
                setRowStatus(prev => ({ ...prev, [rowIndex]: 'success' }));
                dispatch(messageShow(response.data.message));
            }else{
                setRowStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
                dispatch(messageShow(response.data.message));
            }
        } catch(error) {
            setRowStatus(prev => ({ ...prev, [rowIndex]: 'error' }));
            dispatch(messageShow("Error saving contact, Please try again."));
            console.error('Error saving contact:', error);
        }
    };

    const handleSaveAll = async() => {
        if (csvData.length === 0) {
            dispatch(messageShow("No data to save"));
            return;
        }
        
        setSaving(true);
        setSaveProgress(0);
        
        const successResults = [];
        const failedResults = [];
        
        for (let i = 0; i < csvData.length; i++) {
            try {
                setRowStatus(prev => ({ ...prev, [i]: 'saving' }));
                
                const rawData = csvData[i];
                const rowNumber = i + 1;
                
                // Validate row
                const errors = validateRow(rawData);
                if (errors.length > 0) {
                    setRowStatus(prev => ({ ...prev, [i]: 'error' }));
                    failedResults.push({
                        rowNumber,
                        contactName: rawData['Contact Name'] || 'N/A',
                        phoneNumber: rawData['Phone Number'] || 'N/A',
                        error: errors.join(', ')
                    });
                    continue;
                }
                
                const formattedData = formatRowData(rawData);
                
                const response = await saveContact(formattedData);
                if(response.data.success){
                    setRowStatus(prev => ({ ...prev, [i]: 'success' }));
                    successResults.push({
                        rowNumber,
                        contactName: rawData['Contact Name'],
                        phoneNumber: rawData['Phone Number']
                    });
                }else{
                    setRowStatus(prev => ({ ...prev, [i]: 'error' }));
                    failedResults.push({
                        rowNumber,
                        contactName: rawData['Contact Name'] || 'N/A',
                        phoneNumber: rawData['Phone Number'] || 'N/A',
                        error: response.data.message || 'Unknown error'
                    });
                }
            } catch(error) {
                setRowStatus(prev => ({ ...prev, [i]: 'error' }));
                const rawData = csvData[i];
                failedResults.push({
                    rowNumber: i + 1,
                    contactName: rawData['Contact Name'] || 'N/A',
                    phoneNumber: rawData['Phone Number'] || 'N/A',
                    error: error.message || 'Server error occurred'
                });
                console.error(`Error saving row ${i + 1}:`, error);
            }
            
            // Update progress
            setSaveProgress(((i + 1) / csvData.length) * 100);
        }
        
        setSaving(false);
        
        // Show results in dialog
        setUploadResults({
            success: successResults,
            failed: failedResults
        });
        setShowResultDialog(true);
        
        dispatch(messageShow(`Bulk upload completed: ${successResults.length} successful, ${failedResults.length} failed`));
    };


    const handleCellChange = (rowIndex, header, value) => {
        setCsvData(prevData => {
            const newData = [...prevData];
            newData[rowIndex] = { ...newData[rowIndex], [header]: value };
            return newData;
        });
        
        if (rowStatus[rowIndex] === 'error') {
            setRowStatus(prev => {
                const newStatus = { ...prev };
                delete newStatus[rowIndex];
                return newStatus;
            });
        }
    };

    const handleDeleteRow = (rowIndex) => {

        const newData = csvData.filter((_, index) => index !== rowIndex);
        setCsvData(newData);
        
        // Update row status
        const newStatus = {};
        Object.keys(rowStatus).forEach(key => {
            const idx = parseInt(key);
            if (idx < rowIndex) {
                newStatus[idx] = rowStatus[idx];
            } else if (idx > rowIndex) {
                newStatus[idx - 1] = rowStatus[idx];
            }
        });
        setRowStatus(newStatus);
        
        dispatch(messageShow("Row deleted successfully"));
    };

    const getRowStatusColor = (status) => {
        switch(status) {
            case 'success': return 'success';
            case 'error': return 'error';
            case 'saving': return 'warning';
            default: return 'default';
        }
    };

    const getRowStatusLabel = (status) => {
        switch(status) {
            case 'success': return 'Saved';
            case 'error': return 'Failed';
            case 'saving': return 'Saving...';
            default: return 'Pending';
        }
    };

    const handleCloseResultDialog = () => {
        setShowResultDialog(false);
    };

    const totalPages = Math.ceil(csvData.length / rowsPerPage);

    return (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
            <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    Bulk Contact Upload
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Upload a CSV file with proper headers. Empty fields will be handled correctly.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <input
                        accept=".csv"
                        style={{ display: 'none' }}
                        id="csv-file-input" 
                        type="file"
                        onChange={handleFileUpload}
                        disabled={saving}
                    />
                    <label htmlFor="csv-file-input">
                        <Button
                            variant="contained"
                            component="span"
                            startIcon={<CloudUpload />}
                            sx={{ background:"#0d3559" }}
                            disabled={saving}
                        >
                            Upload CSV File
                        </Button>
                    </label>
                    
                    <Button
                        variant="outlined"
                        startIcon={<SaveAlt />}
                        onClick={downloadTemplate}
                        sx={{ color: '#0d3559', borderColor: '#0d3559' }}
                    >
                        Download Sample Template
                    </Button>
                    
                    {csvData.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<SaveAlt />}
                            onClick={handleSaveAll}
                            disabled={saving}
                            sx={{ 
                                background: '#0d3559',
                                '&:hover': {
                                    background: '#092a46'
                                },
                                '&:disabled': {
                                    background: '#ccc'
                                }
                            }}
                        >
                            Save All ({csvData.length} rows)
                        </Button>
                    )}
                </Box>
                
                {saving && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Saving progress: {Math.round(saveProgress)}%
                        </Typography>
                        <LinearProgress variant="determinate" value={saveProgress} />
                    </Box>
                )}
            </Paper>

            {csvData.length > 0 ? (
                <Paper elevation={3}>
                    <TableContainer>
                        <Table size='small' stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell 
                                        sx={{ 
                                            backgroundColor: '#0d3559 !important',
                                            color: '#ffffff !important',
                                            padding: '8px',
                                            fontSize: '0.875rem',
                                            fontWeight: 600
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    {headers.map((header, index) => (
                                        <TableCell 
                                            key={index}
                                            sx={{ 
                                                backgroundColor: '#0d3559 !important',
                                                color: '#ffffff !important',
                                                padding: '8px',
                                                fontSize: '0.875rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            backgroundColor: '#0d3559 !important',
                                            color: '#ffffff !important',
                                            padding:'8px',
                                            fontSize:'0.875rem',
                                            fontWeight: 600
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {csvData
                                    .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                                    .map((row, rowIndex) => {
                                        const actualIndex = rowIndex + (page - 1) * rowsPerPage;
                                        const status = rowStatus[actualIndex];
                                        
                                        return (
                                            <TableRow key={rowIndex} hover>
                                                <TableCell>
                                                    <Chip 
                                                        label={getRowStatusLabel(status)}
                                                        color={getRowStatusColor(status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                {headers.map((header, colIndex) => (
                                                    <TableCell 
                                                        key={colIndex} 
                                                        sx={{ 
                                                            minWidth: '150px',
                                                            maxWidth: '300px', 
                                                        }}
                                                    >
                                                        <input 
                                                            value={row[header] || ''}
                                                            onChange={(e) => handleCellChange(actualIndex, header, e.target.value)}
                                                            disabled={saving || status === 'success'}
                                                            style={{
                                                                width: '100%',
                                                                border: 'none',
                                                                borderBottom: (saving || status === 'success') ? 'none' : '1px solid #ccc',
                                                                background: 'transparent',
                                                                outline: 'none',
                                                                padding: '4px',
                                                                fontFamily: 'inherit',
                                                                fontSize: 'inherit',
                                                                color: (saving || status === 'success') ? '#666' : 'inherit'
                                                            }}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell align="center">
                                                    <IconButton 
                                                        color="primary"
                                                        onClick={() => handleSaveRow(actualIndex)}
                                                        disabled={saving || status === 'success'}
                                                        title="Save this row"
                                                    >
                                                        <Save/>
                                                    </IconButton>
                                                    <IconButton 
                                                        color="error"
                                                        onClick={() => handleDeleteRow(actualIndex)}
                                                        disabled={saving}
                                                        title="Delete this row"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                        sx={{ mt: 2, mb:1, display: 'flex', justifyContent: 'center' }}
                        disabled={saving}
                    />
                </Paper>
            ) : (
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="h6" align="center" color="textSecondary">
                        Upload a CSV file to view data
                    </Typography>
                </Paper>
            )}

            {/* Results Dialog */}
            <Dialog 
                open={showResultDialog} 
                onClose={handleCloseResultDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#0d3559', color: 'white', py: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Bulk Upload Results
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {/* Success Summary */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="h6" color="success.main">
                                Successfully Uploaded: {uploadResults.success.length}
                            </Typography>
                        </Box>
                        {uploadResults.success.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, maxHeight: '200px', overflow: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Contact Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {uploadResults.success.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.rowNumber}</TableCell>
                                                <TableCell>{item.contactName}</TableCell>
                                                <TableCell>{item.phoneNumber}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </Box>

                    {/* Failed Summary */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                            <Typography variant="h6" color="error.main">
                                Failed to Upload: {uploadResults.failed.length}
                            </Typography>
                        </Box>
                        {uploadResults.failed.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflow: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Row #</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Contact Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {uploadResults.failed.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.rowNumber}</TableCell>
                                                <TableCell>{item.contactName}</TableCell>
                                                <TableCell>{item.phoneNumber}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="error">
                                                        {item.error}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={handleCloseResultDialog} 
                        variant="contained" 
                        sx={{ 
                            background: '#0d3559',
                            '&:hover': {
                                background: '#092a46'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default BulkCreate;